import { useEffect, useRef, useState } from 'react'
import type { ColDef, GridApi } from 'ag-grid-community'
import type { TableRow } from './types'

export interface PinColumnsMenuProps {
  getApi: () => GridApi<TableRow> | null | undefined
  columnDefs: ColDef<TableRow>[]
}

export function PinColumnsMenu({ getApi, columnDefs }: PinColumnsMenuProps) {
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState<Set<string>>(new Set())
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleOpen = () => {
    const state = getApi()?.getColumnState() ?? []
    setPinned(new Set(state.filter((c) => c.pinned === 'left').map((c) => c.colId)))
    setOpen((v) => !v)
  }

  const toggle = (field: string) => {
    const next = new Set(pinned)
    if (next.has(field)) next.delete(field)
    else next.add(field)
    setPinned(next)
    getApi()?.applyColumnState({
      state: [{ colId: field, pinned: next.has(field) ? 'left' : null }],
    })
  }

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        onClick={handleOpen}
        style={{
          padding: '4px 10px',
          fontSize: 12,
          border: '1px solid ' + (pinned.size > 0 ? '#93c5fd' : 'var(--border-strong)'),
          borderRadius: 6,
          cursor: 'pointer',
          background: pinned.size > 0 ? 'rgba(59, 130, 246, 0.12)' : 'var(--bg-panel)',
          color: pinned.size > 0 ? '#60a5fa' : 'var(--text-muted)',
        }}
      >
        📌 Закрепить столбцы{pinned.size > 0 ? ` (${pinned.size})` : ''}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            zIndex: 1000,
            minWidth: 220,
            maxHeight: 360,
            overflowY: 'auto',
            padding: '6px 0',
          }}
        >
          <div
            style={{
              padding: '6px 12px 4px',
              fontSize: 11,
              color: 'var(--text-faint)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Закрепить слева
          </div>
          {columnDefs.map((col) => {
            const field = col.field as string
            const label = (col.headerName ?? field).replace(/\n/g, ' ')
            const isPinned = pinned.has(field)
            return (
              <label
                key={field}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '5px 12px',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: 'var(--text)',
                  background: isPinned ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={() => toggle(field)}
                  style={{ cursor: 'pointer' }}
                />
                {label}
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
